/**
 * PresenceAvatars — shows overlapping avatar circles
 * for other users currently viewing/editing a template.
 */

import type { PresenceEntry } from "../../utils/presenceApi";

const COLORS = [
  "#10b981", "#3b82f6", "#f59e0b", "#ef4444",
  "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16",
];

function getColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return COLORS[Math.abs(h) % COLORS.length];
}

export function PresenceAvatars({
  users,
  max = 4,
  size = "sm",
}: {
  users: PresenceEntry[];
  max?: number;
  size?: "sm" | "md";
}) {
  if (users.length === 0) return null;

  const visible = users.slice(0, max);
  const overflow = users.length - max;
  const dim = size === "sm" ? "w-6 h-6" : "w-7 h-7";
  const textSz = size === "sm" ? "text-[9px]" : "text-[10px]";
  const overlap = size === "sm" ? "-ml-1.5" : "-ml-2";

  return (
    <div className="flex items-center" dir="ltr">
      {visible.map((u, i) => (
        <div
          key={u.username}
          className={`${dim} rounded-full border-2 border-white flex items-center justify-center shrink-0 overflow-hidden ${i > 0 ? overlap : ""}`}
          style={{ backgroundColor: getColor(u.username), zIndex: visible.length - i }}
          title={`${u.displayName || u.username} \u2014 \u05E2\u05D5\u05E8\u05DA \u05E2\u05DB\u05E9\u05D9\u05D5`}
        >
          {u.avatarUrl ? (
            <img src={u.avatarUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className={`${textSz} text-white`} style={{ fontWeight: 700 }}>
              {(u.displayName || u.username).charAt(0).toUpperCase()}
            </span>
          )}
        </div>
      ))}
      {overflow > 0 && (
        <div
          className={`${dim} rounded-full border-2 border-white bg-gray-200 flex items-center justify-center ${overlap}`}
          style={{ zIndex: 0 }}
        >
          <span className={`${textSz} text-gray-500`} style={{ fontWeight: 700 }}>
            +{overflow}
          </span>
        </div>
      )}
    </div>
  );
}

/** Compact inline badge showing "X users online" */
export function PresenceBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] border border-emerald-200" style={{ fontWeight: 600 }}>
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
      {count === 1 ? "\u05DE\u05E9\u05EA\u05DE\u05E9 \u05E4\u05E2\u05D9\u05DC" : `${count} \u05E4\u05E2\u05D9\u05DC\u05D9\u05DD`}
    </span>
  );
}